import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useThemeMode } from './hooks/useThemeMode';

// Auth
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './components/auth/LoginPage';
import { SignUpPage } from './components/auth/SignUpPage';
import { RequestAccessPage } from './components/auth/RequestAccessPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';

// Components
import AppHeader from './components/AppHeader';
import AppFooter from './components/AppFooter';
import { Box } from '@mui/material';
import { DREStoreInitializer } from './components/DREStoreInitializer';
import { ValuationStoreInitializer } from './components/ValuationStoreInitializer';
import { ChatKitWidget } from './components/chatkit';

// Pages
import DashboardTriCenario from './pages/DashboardTriCenario';
import PremissasPage from './pages/PremissasPage';
import HistoricoFinanceiro from './pages/HistoricoFinanceiro';
import { DREEditavel } from './pages/DREEditavel';

// Main App with Router
function App() {
  // Hook para gerenciar tema dark/light
  const { theme, mode, toggleTheme } = useThemeMode();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          {/* Inicializar Stores */}
          <DREStoreInitializer />
          <ValuationStoreInitializer />
        
        {/* Toast Notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#1e293b',
              color: '#fff',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
              <Box component="main" sx={{ flex: 1 }}>
                <SignUpPage />
              </Box>
              <AppFooter mode="light" />
            </Box>
          } />
          <Route path="/solicitar-acesso" element={<RequestAccessPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          
          {/* Protected Routes - Nova ordem: Histórico → Dashboard → Premissas */}
          <Route 
            path="/historico" 
            element={
              <ProtectedRoute>
                <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
                  <AppHeader onToggleTheme={toggleTheme} mode={mode} />
                  <Box component="main" sx={{ flex: 1 }}>
                    <HistoricoFinanceiro />
                  </Box>
                  <AppFooter mode={mode} />
                </Box>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
                  <AppHeader onToggleTheme={toggleTheme} mode={mode} />
                  <Box component="main" sx={{ flex: 1 }}>
                    <DashboardTriCenario />
                  </Box>
                  <AppFooter mode={mode} />
                </Box>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/premissas" 
            element={
              <ProtectedRoute>
                <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
                  <AppHeader onToggleTheme={toggleTheme} mode={mode} />
                  <Box component="main" sx={{ flex: 1 }}>
                    <PremissasPage />
                  </Box>
                  <AppFooter mode={mode} />
                </Box>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dre-editavel" 
            element={
              <ProtectedRoute>
                <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
                  <AppHeader onToggleTheme={toggleTheme} mode={mode} />
                  <Box component="main" sx={{ flex: 1 }}>
                    <DREEditavel />
                  </Box>
                  <AppFooter mode={mode} />
                </Box>
              </ProtectedRoute>
            } 
          />
          
          {/* Redirect /resultados to /dashboard for backward compatibility */}
          <Route path="/resultados" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        {/* ChatKit Widget - Disponível em todas as páginas */}
        <ChatKitWidget />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
